import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import AdminLayout from '../components/AdminLayout';
import {
  X, ExternalLink, CheckCircle, XCircle, Clock, Globe, Linkedin,
  FileText, ChevronRight, AlertTriangle, User, Briefcase, MapPin,
  DollarSign, Calendar, Tag
} from 'lucide-react';

const Toast = ({ message, type, onClose }) => {
  useEffect(() => {
    const t = setTimeout(onClose, 4000);
    return () => clearTimeout(t);
  }, [onClose]);
  return (
    <div className={`fixed bottom-6 right-6 z-[100] flex items-center gap-2 px-5 py-3 rounded-xl shadow-xl text-white text-sm font-medium animate-in fade-in slide-in-from-bottom-4 ${type === 'success' ? 'bg-[#004D35]' : 'bg-red-600'}`}>
      {type === 'success' ? <CheckCircle className="w-4 h-4" /> : <XCircle className="w-4 h-4" />}
      {message}
    </div>
  );
};

const normalizeArr = (val) => {
  if (!val) return [];
  if (Array.isArray(val)) return val.filter((v) => v !== null && v !== undefined);
  if (typeof val === 'string') return val.length > 0 ? [val] : [];
  return [];
};

const relativeTime = (date) => {
  if (!date) return '';
  const diff = Math.floor((Date.now() - new Date(date)) / 1000);
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  return new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

const StatusBadge = ({ status }) => {
  const map = {
    new: 'bg-gray-900 text-white',
    approved: 'bg-green-100 text-green-800 border border-green-200',
    rejected: 'bg-red-100 text-red-800 border border-red-200',
  };
  return (
    <span className={`px-2.5 py-1 text-xs font-semibold rounded-full capitalize ${map[status] || 'bg-gray-100 text-gray-700'}`}>
      {status}
    </span>
  );
};

const AdminApplications = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectingApp, setRejectingApp] = useState(null);
  const [rejectionNote, setRejectionNote] = useState('');
  const [adminNotes, setAdminNotes] = useState('');
  const [counts, setCounts] = useState({ new: 0, approved: 0, rejected: 0 });
  const [toast, setToast] = useState(null);
  const [approving, setApproving] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [emailEditing, setEmailEditing] = useState(false);
  const [emailValue, setEmailValue] = useState('');
  const [savingEmail, setSavingEmail] = useState(false);
  const [invitationWarning, setInvitationWarning] = useState('');
  const [retryingInvitation, setRetryingInvitation] = useState(false);

  const statusFilter = searchParams.get('status') || 'new';

  const showToast = useCallback((message, type = 'success') => setToast({ message, type }), []);

  const fetchApplications = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`/api/admin/applications?status=${statusFilter}`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setApplications(data.applications || []);
        setCounts(data.counts || { new: 0, approved: 0, rejected: 0 });
      }
    } catch (e) {
      showToast('Error loading applications', 'error');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, showToast]);

  useEffect(() => {
    if (user?.role !== 'admin') { navigate('/'); return; }
    fetchApplications();
  }, [user, fetchApplications, navigate]);

  useEffect(() => {
    setEmailEditing(false);
    setEmailValue('');
    setInvitationWarning('');
  }, [selected?.id]);

  const handleApprove = async (id) => {
    setApproving(true);
    try {
      const res = await fetch(`/api/admin/applications/${id}/approve`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({})
      });
      const data = await res.json();
      if (res.ok) {
        if (data.invitationSent === false) {
          setInvitationWarning(data.warning || 'Advisor approved, but the invitation email was not sent.');
          setSelected(current => current ? { ...current, status: 'approved' } : current);
          showToast(data.warning || 'Approved, but invitation email was not sent.', 'error');
        } else {
          showToast(data.message || 'Approved successfully');
          setSelected(null);
        }
        fetchApplications();
      } else {
        showToast(data.error || 'Failed to approve', 'error');
      }
    } catch (e) {
      showToast('Network error', 'error');
    } finally {
      setApproving(false);
    }
  };

  const retryInvitation = async () => {
    if (!selected?.id) return;
    setRetryingInvitation(true);
    try {
      const res = await fetch(`/api/admin/advisors/${selected.id}/resend-invite`, {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data.invitationSent === false || data.success === false) throw new Error(data.warning || data.error || 'Invitation could not be resent.');
      setInvitationWarning('');
      showToast(data.message || 'Invitation resent');
    } catch (err) { setInvitationWarning(err.message || 'Invitation could not be resent.'); }
    finally { setRetryingInvitation(false); }
  };

  const handleReject = async () => {
    setRejecting(true);
    try {
      const res = await fetch(`/api/admin/applications/${rejectingApp.id}/reject`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ rejection_note: rejectionNote })
      });
      if (res.ok) {
        showToast('Application rejected');
        setShowRejectModal(false);
        setRejectingApp(null);
        setSelected(null);
        setRejectionNote('');
        fetchApplications();
      } else {
        const d = await res.json();
        showToast(d.error || 'Failed to reject', 'error');
      }
    } catch (e) {
      showToast('Network error', 'error');
    } finally {
      setRejecting(false);
    }
  };

  const handleEmailSave = async () => {
    if (!emailValue.trim()) return;
    setSavingEmail(true);
    try {
      const res = await fetch(`/api/admin/applications/${selected.id}/email`, {
        method: 'PATCH', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: emailValue.trim() })
      });
      const data = await res.json();
      if (res.ok) {
        setSelected((prev) => ({ ...prev, email: data.email }));
        setEmailEditing(false);
        showToast('Email updated');
      } else {
        showToast(data.error || 'Failed to update email', 'error');
      }
    } catch (e) {
      showToast('Network error', 'error');
    } finally {
      setSavingEmail(false);
    }
  };

  const saveAdminNotes = async (appId, notes) => {
    try {
      await fetch(`/api/admin/applications/${appId}/notes`, {
        method: 'PATCH', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ admin_notes: notes })
      });
    } catch (e) {
      console.error('Failed to save notes:', e);
    }
  };

  const FILTERS = [
    { key: 'new', label: `New`, count: counts.new },
    { key: 'approved', label: `Approved`, count: counts.approved },
    { key: 'rejected', label: `Rejected`, count: counts.rejected },
    { key: 'all', label: 'All', count: null },
  ];

  return (
    <AdminLayout activeTab="applications">
      <div className="space-y-6">
        {/* Header */}
        <h1 className="text-2xl font-bold text-gray-900">Applications</h1>

        {/* Stat Cards */}
        <div className="grid grid-cols-3 gap-4">
          {[
            { label: 'New', value: counts.new, color: 'bg-gray-900 text-white' },
            { label: 'Approved', value: counts.approved, color: 'bg-green-50 text-green-800 border border-green-200' },
            { label: 'Rejected', value: counts.rejected, color: 'bg-red-50 text-red-800 border border-red-200' },
          ].map((s) => (
            <div key={s.label} className={`rounded-xl p-5 ${s.color}`}>
              <p className="text-3xl font-bold">{s.value}</p>
              <p className="text-sm font-medium mt-1 opacity-80">{s.label}</p>
            </div>
          ))}
        </div>

        {/* Filter Pills */}
        <div className="flex gap-2">
          {FILTERS.map((f) => (
            <button
              key={f.key}
              onClick={() => setSearchParams({ status: f.key })}
              className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                statusFilter === f.key
                  ? 'bg-gray-900 text-white'
                  : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
              }`}
            >
              {f.label}{f.count !== null ? ` (${f.count})` : ''}
            </button>
          ))}
        </div>

        {/* Table */}
        {loading ? (
          <div className="flex justify-center items-center h-48">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900" />
          </div>
        ) : applications.length === 0 ? (
          <div className="bg-white rounded-xl border border-[#e8e5e0] p-12 text-center">
            <FileText className="w-10 h-10 text-gray-300 mx-auto mb-3" />
            <h3 className="text-base font-medium text-gray-900">No {statusFilter === 'all' ? '' : statusFilter} applications</h3>
          </div>
        ) : (
          <div className="bg-white rounded-xl border border-[#e8e5e0] overflow-hidden">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-gray-50 border-b border-[#e8e5e0]">
                  <th className="px-5 py-3.5 text-xs font-semibold text-gray-500 uppercase tracking-wider">Applicant</th>
                  <th className="px-5 py-3.5 text-xs font-semibold text-gray-500 uppercase tracking-wider">Category</th>
                  <th className="px-5 py-3.5 text-xs font-semibold text-gray-500 uppercase tracking-wider">Profile</th>
                  <th className="px-5 py-3.5 text-xs font-semibold text-gray-500 uppercase tracking-wider">Location</th>
                  <th className="px-5 py-3.5 text-xs font-semibold text-gray-500 uppercase tracking-wider">Applied</th>
                  <th className="px-5 py-3.5 text-xs font-semibold text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-5 py-3.5 text-xs font-semibold text-gray-500 uppercase tracking-wider text-right">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#e8e5e0]">
                {applications.map((app) => (
                  <tr key={app.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-5 py-4">
                      <p className="font-semibold text-gray-900 text-sm">{app.first_name} {app.last_name}</p>
                      <p className="text-xs text-gray-400 mt-0.5">{app.email}</p>
                    </td>
                    <td className="px-5 py-4">
                      {app.category && (
                        <span className="px-2.5 py-1 bg-[#e8f5ee] text-[#004D35] text-xs font-medium rounded-full">
                          {app.category}
                        </span>
                      )}
                    </td>
                    <td className="px-5 py-4">
                      {app.has_profile_data ? (
                        <span className="px-2.5 py-1 bg-[#a8f0c6] text-[#004D35] text-xs font-semibold rounded-full">
                          Profile ✓
                        </span>
                      ) : (
                        <span className="text-xs text-gray-400">—</span>
                      )}
                    </td>
                    <td className="px-5 py-4 text-sm text-gray-600">{app.location || '—'}</td>
                    <td className="px-5 py-4 text-sm text-gray-500">{relativeTime(app.created_at)}</td>
                    <td className="px-5 py-4"><StatusBadge status={app.status} /></td>
                    <td className="px-5 py-4 text-right">
                      <button
                        onClick={() => { setSelected(app); setAdminNotes(app.admin_notes || ''); }}
                        className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-full hover:bg-gray-50 transition-colors"
                      >
                        Review <ChevronRight className="w-3.5 h-3.5" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Detail Panel */}
      {selected && (
        <div className="fixed inset-0 z-[60] overflow-hidden">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setSelected(null)} />
          <div className="absolute inset-y-0 right-0 flex max-w-full">
            <div className="w-screen max-w-[560px] bg-[#fdfcfb] shadow-2xl flex flex-col">
              {/* Panel Header */}
              <div className="flex items-start justify-between p-6 border-b border-[#e8e5e0] bg-white">
                <div>
                  <h2 className="text-xl font-serif text-gray-900">{selected.first_name} {selected.last_name}</h2>
                  {emailEditing ? (
                    <div className="mt-1 flex items-center gap-2">
                      <input
                        type="email"
                        value={emailValue}
                        onChange={(e) => setEmailValue(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') handleEmailSave(); if (e.key === 'Escape') setEmailEditing(false); }}
                        autoFocus
                        className="text-sm border border-[#004D35] rounded-lg px-2.5 py-1 focus:ring-2 focus:ring-[#004D35] outline-none w-56"
                      />
                      <button
                        onClick={handleEmailSave}
                        disabled={savingEmail}
                        className="text-xs font-semibold text-white bg-[#004D35] px-3 py-1.5 rounded-full disabled:opacity-50"
                      >
                        {savingEmail ? 'Saving…' : 'Save'}
                      </button>
                      <button onClick={() => setEmailEditing(false)} className="text-xs text-gray-400 hover:text-gray-600">Cancel</button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-1.5 mt-0.5">
                      <p className="text-sm text-gray-500">{selected.email}</p>
                      {selected.status === 'new' && (
                        <button
                          onClick={() => { setEmailValue(selected.email); setEmailEditing(true); }}
                          className="text-[10px] font-semibold text-[#004D35] hover:underline"
                        >
                          Edit
                        </button>
                      )}
                    </div>
                  )}
                  <div className="mt-2 flex items-center gap-2">
                    <StatusBadge status={selected.status} />
                    <span className="text-xs text-gray-400">{relativeTime(selected.created_at)}</span>
                  </div>
                </div>
                <button onClick={() => setSelected(null)} className="p-2 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100">
                  <X className="w-5 h-5" />
                </button>
              </div>

              {invitationWarning && (
                <div role="alert" className="mx-6 mt-5 rounded-xl border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900">
                  <p className="font-semibold">{invitationWarning}</p>
                  <p className="mt-1 text-xs">The advisor was approved. Retry the account invitation email or contact them directly.</p>
                  <button onClick={retryInvitation} disabled={retryingInvitation} className="mt-3 rounded-full bg-[#004D35] px-4 py-2 text-xs font-semibold text-white disabled:opacity-60">
                    {retryingInvitation ? 'Resending…' : 'Retry invitation'}
                  </button>
                </div>
              )}

              <div className="flex-1 overflow-y-auto p-6 space-y-7">
                {/* Verification Section */}
                <section>
                  <div className="flex items-center gap-2 mb-3">
                    <h3 className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Verification</h3>
                    <span className="text-[9px] font-bold text-amber-600 bg-amber-50 px-2 py-0.5 rounded-full uppercase tracking-wider">Admin only</span>
                  </div>
                  <div className="grid grid-cols-2 gap-4 bg-white border border-[#e8e5e0] rounded-2xl p-5">
                    <div>
                      <p className="text-[10px] text-gray-400 uppercase tracking-wider mb-1">Category</p>
                      <p className="text-sm font-medium text-gray-900">{selected.category || '—'}</p>
                    </div>
                    <div>
                      <p className="text-[10px] text-gray-400 uppercase tracking-wider mb-1">Credential #</p>
                      <p className="text-sm font-medium text-gray-900">{selected.credential_number || 'Not provided'}</p>
                    </div>
                    <div>
                      <p className="text-[10px] text-gray-400 uppercase tracking-wider mb-1">Experience</p>
                      <p className="text-sm font-medium text-gray-900">{selected.years_experience || '—'}</p>
                    </div>
                    <div>
                      <p className="text-[10px] text-gray-400 uppercase tracking-wider mb-1">Location</p>
                      <p className="text-sm font-medium text-gray-900">{selected.location || '—'}</p>
                    </div>
                    <div className="col-span-2">
                      <p className="text-[10px] text-gray-400 uppercase tracking-wider mb-1">Remote</p>
                      <p className="text-sm font-medium text-gray-900">{selected.remote_available || 'Not specified'}</p>
                    </div>
                    {(selected.website_url || selected.linkedin_url) && (
                      <div className="col-span-2 pt-2 border-t border-gray-100 flex flex-col gap-2">
                        {selected.website_url && (
                          <a href={selected.website_url} target="_blank" rel="noopener noreferrer"
                            className="flex items-center gap-1.5 text-sm text-[#004D35] hover:underline">
                            <Globe className="w-3.5 h-3.5" /> Website <ExternalLink className="w-3 h-3" />
                          </a>
                        )}
                        {selected.linkedin_url && (
                          <a href={selected.linkedin_url} target="_blank" rel="noopener noreferrer"
                            className="flex items-center gap-1.5 text-sm text-[#004D35] hover:underline">
                            <Linkedin className="w-3.5 h-3.5" /> LinkedIn <ExternalLink className="w-3 h-3" />
                          </a>
                        )}
                      </div>
                    )}
                  </div>
                </section>

                {/* Profile Head Start */}
                <section>
                  <h3 className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-3">Profile Head Start</h3>
                  {!selected.has_profile_data ? (
                    <div className="bg-gray-50 border border-[#e8e5e0] rounded-2xl p-5 text-center">
                      <p className="text-sm text-gray-500 italic">Advisor skipped the profile section.</p>
                    </div>
                  ) : (
                    <div className="bg-white border border-[#e8e5e0] rounded-2xl p-5 space-y-4">
                      {selected.headline && (
                        <div>
                          <p className="text-[10px] text-gray-400 uppercase tracking-wider mb-1">Headline</p>
                          <p className="text-sm font-semibold text-gray-900">"{selected.headline}"</p>
                        </div>
                      )}
                      {selected.bio && (
                        <div>
                          <p className="text-[10px] text-gray-400 uppercase tracking-wider mb-1">Bio</p>
                          <p className="text-sm text-gray-700 leading-relaxed">{selected.bio}</p>
                        </div>
                      )}
                      {selected.why_i_do_this && (
                        <div>
                          <p className="text-[10px] text-gray-400 uppercase tracking-wider mb-1">Why I Do This</p>
                          <p className="text-sm text-gray-700 leading-relaxed">{selected.why_i_do_this}</p>
                        </div>
                      )}
                      {normalizeArr(selected.specialties).length > 0 && (
                        <div>
                          <p className="text-[10px] text-gray-400 uppercase tracking-wider mb-2">Specialties</p>
                          <div className="flex flex-wrap gap-1.5">
                            {normalizeArr(selected.specialties).map((s, i) => (
                              <span key={i} className="px-2.5 py-1 bg-[#e8f5ee] text-[#004D35] text-xs font-medium rounded-full">{s}</span>
                            ))}
                          </div>
                        </div>
                      )}
                      {normalizeArr(selected.industries_served).length > 0 && (
                        <div>
                          <p className="text-[10px] text-gray-400 uppercase tracking-wider mb-2">Industries</p>
                          <div className="flex flex-wrap gap-1.5">
                            {normalizeArr(selected.industries_served).map((s, i) => (
                              <span key={i} className="px-2.5 py-1 bg-gray-100 text-gray-700 text-xs font-medium rounded-full">{s}</span>
                            ))}
                          </div>
                        </div>
                      )}
                      <div className="grid grid-cols-2 gap-4 pt-2 border-t border-gray-50">
                        {selected.hourly_rate && (
                          <div>
                            <p className="text-[10px] text-gray-400 uppercase tracking-wider mb-1">Rate</p>
                            <p className="text-sm font-medium text-gray-900">${selected.hourly_rate}/hr</p>
                          </div>
                        )}
                        {selected.pricing_type && (
                          <div>
                            <p className="text-[10px] text-gray-400 uppercase tracking-wider mb-1">Pricing Type</p>
                            <p className="text-sm font-medium text-gray-900">{selected.pricing_type}</p>
                          </div>
                        )}
                        {selected.calendly_url && (
                          <div className="col-span-2">
                            <a href={selected.calendly_url} target="_blank" rel="noopener noreferrer"
                              className="flex items-center gap-1.5 text-sm text-[#004D35] hover:underline">
                              <Calendar className="w-3.5 h-3.5" /> Calendly <ExternalLink className="w-3 h-3" />
                            </a>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </section>

                {/* Admin Notes */}
                <section>
                  <h3 className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-2">Admin Notes</h3>
                  <p className="text-[10px] text-gray-400 mb-2 italic">Internal notes — never shared with advisor</p>
                  <textarea
                    value={adminNotes}
                    onChange={(e) => setAdminNotes(e.target.value)}
                    onBlur={() => saveAdminNotes(selected.id, adminNotes)}
                    placeholder="Add internal notes..."
                    className="w-full bg-white border border-[#e8e5e0] rounded-xl p-3.5 text-sm focus:ring-2 focus:ring-[#004D35] focus:border-transparent outline-none min-h-[100px] resize-none"
                  />
                </section>
              </div>

              {/* Sticky Action Bar */}
              {selected.status === 'new' && (
                <div className="p-5 border-t border-[#e8e5e0] bg-white flex gap-3">
                  <button
                    onClick={() => handleApprove(selected.id)}
                    disabled={approving}
                    className="flex-1 bg-[#004D35] hover:bg-[#003d2a] disabled:opacity-60 text-white py-3 px-5 rounded-full font-semibold text-sm transition-all flex items-center justify-center gap-2"
                  >
                    <CheckCircle className="w-4 h-4" />
                    {approving ? 'Approving...' : 'Approve →'}
                  </button>
                  <button
                    onClick={() => { setRejectingApp(selected); setShowRejectModal(true); }}
                    className="flex-1 bg-white border-2 border-red-500 text-red-500 hover:bg-red-50 py-3 px-5 rounded-full font-semibold text-sm transition-all flex items-center justify-center gap-2"
                  >
                    <XCircle className="w-4 h-4" />
                    Reject
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Reject Modal */}
      {showRejectModal && rejectingApp && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setShowRejectModal(false)} />
          <div className="bg-white rounded-3xl shadow-2xl max-w-md w-full p-7 relative animate-in zoom-in-95 duration-200">
            <h2 className="text-xl font-serif text-gray-900 mb-1">Reject application</h2>
            <p className="text-sm text-gray-500 mb-5">
              Rejecting <span className="font-semibold">{rejectingApp.first_name} {rejectingApp.last_name}</span>. A rejection email will be sent.
            </p>
            <div className="space-y-5">
              <div>
                <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest block mb-2">
                  Reason for rejection (optional — included in email)
                </label>
                <textarea
                  value={rejectionNote}
                  onChange={(e) => setRejectionNote(e.target.value)}
                  placeholder="e.g. We require at least 5 years of experience..."
                  className="w-full border border-[#e8e5e0] rounded-xl p-3.5 text-sm focus:ring-2 focus:ring-red-500 focus:border-transparent outline-none min-h-[110px] resize-none"
                />
              </div>
              <div className="flex gap-3">
                <button
                  onClick={() => setShowRejectModal(false)}
                  className="flex-1 px-5 py-3 border border-[#e8e5e0] text-gray-600 font-semibold rounded-full hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleReject}
                  disabled={rejecting}
                  className="flex-1 px-5 py-3 bg-red-600 text-white font-semibold rounded-full hover:bg-red-700 disabled:opacity-60 transition-colors"
                >
                  {rejecting ? 'Sending...' : 'Send rejection'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
    </AdminLayout>
  );
};

export default AdminApplications;
